/**
 * Cloudflare R2 region hints - all 4 characters for consistent uploadId structure
 */
export type CloudflareRegionHint = 'weur' | 'enam' | 'apac' | 'ocea';

export const CLOUDFLARE_REGIONS_ORDER: CloudflareRegionHint[] = ['weur', 'enam', 'apac', 'ocea'];

export const CLOUDFLARE_REGION_DISPLAY_NAMES: Record<CloudflareRegionHint, string> = {
  weur: 'Europe',
  enam: 'North America',
  apac: 'Asia Pacific',
  ocea: 'Oceania',
};

/**
 * Azure region codes - all 4 characters
 */
export type AzureRegionCode =
  | 'weeu' | 'noeu' | 'noea' | 'frce' | 'frso' | 'gewc' | 'geno' | 'swno' | 'swce' | 'swwe'
  | 'ukso' | 'ukwe' | 'safn' | 'safw' | 'atea' | 'bece' | 'itno' | 'poce' | 'spce' | 'isce'
  | 'qace' | 'uano' | 'uace' | 'nowe' | 'brso' | 'brse' | 'cace' | 'caea' | 'ceus' | 'cueu'
  | 'eaus' | 'eus2' | 'e2eu' | 'ncus' | 'scus' | 'wcus' | 'weus' | 'wus2' | 'wus3' | 'mece'
  | 'chce' | 'eaas' | 'seas' | 'jaea' | 'jawe' | 'koce' | 'koso' | 'cein' | 'soin' | 'wein'
  | 'ince' | 'mawe' | 'auea' | 'ause' | 'auce' | 'auc2' | 'zeno';

export const AZURE_REGION_DISPLAY_NAMES: Record<string, string> = {
  weeu: 'Netherlands - Amsterdam',
  noeu: 'Ireland - Dublin',
  noea: 'Norway - Oslo',
  frce: 'France - Paris',
  frso: 'France - Marseille',
  gewc: 'Germany - Frankfurt',
  geno: 'Germany - Berlin',
  swno: 'Switzerland - Zurich',
  swce: 'Sweden - Gävle',
  swwe: 'Switzerland - Geneva',
  ukso: 'UK - London',
  ukwe: 'UK - Cardiff',
  safn: 'South Africa - Johannesburg',
  safw: 'South Africa - Cape Town',
  atea: 'Austria - Vienna',
  bece: 'Belgium - Brussels',
  itno: 'Italy - Milan',
  poce: 'Poland - Warsaw',
  spce: 'Spain - Madrid',
  isce: 'Israel - Tel Aviv',
  qace: 'Qatar - Doha',
  uano: 'UAE - Dubai',
  uace: 'UAE - Abu Dhabi',
  nowe: 'Norway - Stavanger',
  brso: 'Brazil - São Paulo',
  brse: 'Brazil - Rio de Janeiro',
  cace: 'Canada - Toronto',
  caea: 'Canada - Quebec City',
  ceus: 'US - Iowa',
  // cueu: 'US - Iowa', // Duplicate city (EUAP region)
  eaus: 'US - Virginia',
  // eus2: 'US - Virginia', // Duplicate city
  // e2eu: 'US - Virginia', // Duplicate city (EUAP region)
  ncus: 'US - Illinois',
  scus: 'US - Texas',
  wcus: 'US - Wyoming',
  weus: 'US - California',
  wus2: 'US - Washington',
  wus3: 'US - Arizona',
  mece: 'Mexico - Querétaro',
  chce: 'Chile - Santiago',
  eaas: 'Hong Kong - Hong Kong',
  seas: 'Singapore - Singapore',
  jaea: 'Japan - Tokyo',
  jawe: 'Japan - Osaka',
  koce: 'Korea - Seoul',
  koso: 'Korea - Busan',
  cein: 'India - Pune',
  soin: 'India - Chennai',
  wein: 'India - Mumbai',
  ince: 'Indonesia - Jakarta',
  mawe: 'Malaysia - Kuala Lumpur',
  auea: 'Australia - Sydney',
  ause: 'Australia - Melbourne',
  auce: 'Australia - Canberra',
  // auc2: 'Australia - Canberra', // Duplicate city
  zeno: 'New Zealand - Auckland',
};

/**
 * Maps ISO 3166-1 alpha-2 country codes to Azure region codes
 * Used to auto-select the appropriate region based on user's country
 */
export const COUNTRY_TO_AZURE_REGION: Record<string, AzureRegionCode> = {
  NL: 'weeu', // Netherlands
  IE: 'noeu', // Ireland
  NO: 'noea', // Norway
  FR: 'frce', // France
  DE: 'gewc', // Germany
  CH: 'swno', // Switzerland
  SE: 'swce', // Sweden
  GB: 'ukso', // United Kingdom
  ZA: 'safn', // South Africa
  AT: 'atea', // Austria
  BE: 'bece', // Belgium
  IT: 'itno', // Italy
  PL: 'poce', // Poland
  ES: 'spce', // Spain
  IL: 'isce', // Israel
  QA: 'qace', // Qatar
  AE: 'uano', // United Arab Emirates
  BR: 'brso', // Brazil
  CA: 'cace', // Canada
  US: 'eaus', // United States
  MX: 'mece', // Mexico
  CL: 'chce', // Chile
  HK: 'eaas', // Hong Kong
  SG: 'seas', // Singapore
  JP: 'jaea', // Japan
  KR: 'koce', // Korea
  IN: 'cein', // India
  ID: 'ince', // Indonesia
  MY: 'mawe', // Malaysia
  AU: 'auea', // Australia
  NZ: 'zeno', // New Zealand
};

/**
 * Maps Azure region codes to their nearest Cloudflare R2 region hint
 */
export const AZURE_TO_CLOUDFLARE_REGION: Record<string, CloudflareRegionHint> = {
  // Europe
  WEEU: 'weur',
  NOEU: 'weur',
  NOEA: 'weur',
  FRCE: 'weur',
  FRSO: 'weur',
  GEWC: 'weur',
  GENO: 'weur',
  SWNO: 'weur',
  SWCE: 'weur',
  SWWE: 'weur',
  UKSO: 'weur',
  UKWE: 'weur',
  SAFN: 'weur',
  SAFW: 'weur',
  ATEA: 'weur',
  BECE: 'weur',
  ITNO: 'weur',
  POCE: 'weur',
  SPCE: 'weur',
  ISCE: 'weur',
  QACE: 'weur',
  UANO: 'weur',
  UACE: 'weur',
  NOWE: 'weur',
  // Americas
  BRSO: 'enam',
  BRSE: 'enam',
  CACE: 'enam',
  CAEA: 'enam',
  CEUS: 'enam',
  CUEU: 'enam',
  EAUS: 'enam',
  EUS2: 'enam',
  E2EU: 'enam',
  NCUS: 'enam',
  SCUS: 'enam',
  WCUS: 'enam',
  WEUS: 'enam',
  WUS2: 'enam',
  WUS3: 'enam',
  MECE: 'enam',
  CHCE: 'enam',
  // Asia Pacific
  EAAS: 'apac',
  SEAS: 'apac',
  JAEA: 'apac',
  JAWE: 'apac',
  KOCE: 'apac',
  KOSO: 'apac',
  CEIN: 'apac',
  SOIN: 'apac',
  WEIN: 'apac',
  INCE: 'apac',
  MAWE: 'apac',
  // Oceania
  AUEA: 'ocea',
  AUSE: 'ocea',
  AUCE: 'ocea',
  AUC2: 'ocea',
  ZENO: 'ocea',
};

/**
 * Storage provider type
 */
export type StorageProvider = 'azure' | 'cloudflare';

/**
 * Unified region selection representing either Azure or Cloudflare storage
 */
export interface StorageRegion {
  code: string; // 4-char region code
  provider: StorageProvider;
  displayName: string;
}

/**
 * Get all available Cloudflare regions as StorageRegion objects
 */
export function getCloudflareStorageRegions(): StorageRegion[] {
  return CLOUDFLARE_REGIONS_ORDER.map((code) => ({
    code,
    provider: 'cloudflare' as StorageProvider,
    displayName: CLOUDFLARE_REGION_DISPLAY_NAMES[code],
  }));
}

/**
 * Get all available Azure regions as StorageRegion objects
 */
export function getAzureStorageRegions(availableRegions: string[]): StorageRegion[] {
  return availableRegions
    .map((code) => code.toLowerCase())
    .filter((code) => AZURE_REGION_DISPLAY_NAMES[code] && !isCloudflareRegion(code))
    .map((code) => ({
      code,
      provider: 'azure' as StorageProvider,
      displayName: AZURE_REGION_DISPLAY_NAMES[code] || code.toUpperCase(),
    }));
}

/**
 * Get display name for a region code
 */
export function getRegionDisplayName(code: string): string {
  const lower = code.toLowerCase();
  return CLOUDFLARE_REGION_DISPLAY_NAMES[lower as CloudflareRegionHint] 
    || AZURE_REGION_DISPLAY_NAMES[lower] 
    || code.toUpperCase();
}

/**
 * Check if a region code is a valid Cloudflare region
 */
export function isCloudflareRegion(code: string): code is CloudflareRegionHint {
  return CLOUDFLARE_REGIONS_ORDER.includes(code.toLowerCase() as CloudflareRegionHint);
}

/**
 * Check if a region code is a valid Azure region
 */
export function isAzureRegion(code: string): boolean {
  return code.toLowerCase() in AZURE_REGION_DISPLAY_NAMES;
}

/**
 * Get Cloudflare region hint for an Azure region (for fallback/routing)
 */
export function getCloudflareRegionForAzure(azureRegion: string | undefined): CloudflareRegionHint | undefined {
  if (!azureRegion) {
    return undefined;
  }
  return AZURE_TO_CLOUDFLARE_REGION[azureRegion.toUpperCase()];
}

/**
 * Determine storage provider from region code
 */
export function getStorageProvider(regionCode: string): StorageProvider {
  return isCloudflareRegion(regionCode) ? 'cloudflare' : 'azure';
}
